'use client';

import { motion } from 'motion/react';
import { Clock, Calendar, User, ExternalLink, Link2, CheckCircle2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import Link from 'next/link';
// import { SEO } from "../components/SEO";
import { ResourceSection, Source } from '../data/resources';
import { ResponsiveHeader } from '../components/ResponsiveHeader';
import { ResponsiveFooter } from '../components/ResponsiveFooter';
import Script from 'next/script';

interface ArticleTemplateProps {
  title: string;
  category: string;
  introduction: string;
  heroImage: string;
  sections: ResourceSection[];

  author: string;
  publishDate: string;
  readingEstimation: number;
  color: string;
  takeaways: string;
  description: string;
  sources?: Source[];
}

export function ArticleTemplate({
  title,
  category,
  introduction,
  heroImage,
  sections,

  author,
  publishDate,
  readingEstimation,
  color,
  takeaways,
  description,
  sources,
}: ArticleTemplateProps) {
  const [activeSection, setActiveSection] = useState('introduction');
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Initialize state from media query
    handleChange(mediaQuery as unknown as MediaQueryListEvent);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sectionIds = ['introduction', ...sections.map((_, i) => `section-${i}`), 'conclusion'];

      for (const sectionId of sectionIds) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 24;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const getColorHex = (colorName: string) => {
    const colors: { [key: string]: string } = {
      purple: '#a855f7',
      cyan: '#22d3ee',
      emerald: '#10b981',
      yellow: '#fbbf24',
      pink: '#ec4899',
      violet: '#8b5cf6',
      blue: '#3b82f6',
      indigo: '#6366f1',
      orange: '#f97316',
    };
    return colors[colorName] || colors.orange;
  };

  // Helper to get gradient styles directly (bypassing Tailwind class generation issues)
  const getThemeStyles = (colorName: string) => {
    const colors: Record<string, { from: string; to: string; fromRgb: string; toRgb: string }> = {
      purple: { from: '#9333ea', to: '#db2777', fromRgb: '147, 51, 234', toRgb: '219, 39, 119' },
      cyan: { from: '#06b6d4', to: '#3b82f6', fromRgb: '6, 182, 212', toRgb: '59, 130, 246' },
      emerald: { from: '#10b981', to: '#14b8a6', fromRgb: '16, 185, 129', toRgb: '20, 184, 166' },
      yellow: { from: '#eab308', to: '#f97316', fromRgb: '234, 179, 8', toRgb: '249, 115, 22' },
      pink: { from: '#ec4899', to: '#f43f5e', fromRgb: '236, 72, 153', toRgb: '244, 63, 94' },
      violet: { from: '#7c3aed', to: '#c026d3', fromRgb: '124, 58, 237', toRgb: '192, 38, 211' },
      blue: { from: '#2563eb', to: '#4f46e5', fromRgb: '37, 99, 235', toRgb: '79, 70, 229' },
      indigo: { from: '#4f46e5', to: '#9333ea', fromRgb: '79, 70, 229', toRgb: '147, 51, 234' },
      orange: { from: '#f97316', to: '#ef4444', fromRgb: '249, 115, 22', toRgb: '239, 68, 68' },
    };

    const c = colors[colorName] || colors.purple;

    return {
      takeaways: {
        backgroundImage: `linear-gradient(to bottom right, rgba(${c.fromRgb}, 0.2), rgba(${c.toRgb}, 0.2))`,
      },
      button: {
        backgroundImage: `linear-gradient(to right, ${c.from}, ${c.to})`,
      },
    };
  };

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(publishDate));

  const themeStyles = getThemeStyles(color);
  const baseColor = getColorHex(color);

  return (
    <div className="min-h-screen bg-[#18181b] flex flex-col relative">
      {/* BlogPosting Structured Data for Rich Results */}
      <Script id="article-schema" type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: title,
          image: heroImage,
          author: {
            '@type': 'Person',
            name: author,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Zumify',
            logo: {
              '@type': 'ImageObject',
              url: 'https://zumify.co/logo.png',
            },
          },
          datePublished: publishDate,
          dateModified: publishDate,
          description: description,
          articleSection: category,
        })}
      </Script>

      <ResponsiveHeader />

      {/* Hero Section with Image */}
      <section className="relative w-full min-h-[500px] lg:min-h-[600px] flex items-center justify-center bg-zinc-900 overflow-hidden">
        {/* Hero Image & Overlays */}
        <div className="absolute inset-0 z-0">
          <img src={heroImage} alt={title} className="w-full h-full object-cover" loading="eager" />
          {/* Resource-specific gradient overlay */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background: `linear-gradient(135deg, ${baseColor}, ${baseColor}88)`,
            }}
          />
          {/* Bottom fade for readability and transition */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 50%, #18181b 100%)`,
            }}
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full pt-32 pb-20 mt-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              {/* Category Badge */}
              <div className="mb-6 flex justify-center">
                <span
                  className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white shadow-lg"
                  style={{ background: `linear-gradient(to right, ${baseColor}, ${baseColor}cc)` }}
                >
                  {category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight text-center">
                {title}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-white">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">Written by {author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{formattedDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{readingEstimation} min read</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="relative px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto flex justify-center gap-8 xl:gap-12">
          {/* Table of Contents - Sticky on Left Side */}
          {/* Table of Contents - Sticky on Left Side */}
          <aside
            style={{
              position: 'sticky',
              top: '6rem',
              alignSelf: 'flex-start',
              display: isDesktop ? 'block' : 'none',
            }}
            className="w-[240px] flex-shrink-0 pr-4 max-h-[calc(100vh-6rem)] overflow-y-auto"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="m-[0px]">
                <h2 className="text-lg font-bold text-white mb-4">Table of Contents</h2>
                <nav className="space-y-4">
                  {[
                    { id: 'introduction', label: 'Introduction' },
                    ...sections.map((section, index) => ({
                      id: `section-${index}`,
                      label: section.title,
                    })),
                  ].map((item) => (
                    <motion.button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`
                        block w-full text-left px-4 py-2 rounded-lg text-sm transition-all duration-300
                        ${
                          activeSection === item.id
                            ? 'text-white font-semibold shadow-lg'
                            : 'text-white hover:text-white hover:bg-white/5'
                        }
                      `}
                      style={{
                        background:
                          activeSection === item.id
                            ? `linear-gradient(to right, ${baseColor}, ${baseColor}cc)`
                            : undefined,
                      }}
                      whileHover={{ x: 4 }}
                    >
                      {item.label}
                    </motion.button>
                  ))}
                </nav>
              </div>
            </motion.div>
          </aside>

          {/* Main Content Column - RIGHT SIDE */}
          <div className="flex-1 max-w-3xl">
            {/* Introduction */}
            <motion.div
              id="introduction"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12 lg:mb-16 scroll-mt-32 text-center lg:text-left"
            >
              <div className="space-y-6">
                {introduction
                  .split('\n\n')
                  .filter((p) => p.trim() !== '')
                  .map((paragraph, i) => (
                    <p
                      key={i}
                      className="text-lg lg:text-xl text-white/90 leading-relaxed"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {paragraph}
                    </p>
                  ))}
              </div>
            </motion.div>

            {/* Dynamic Sections */}
            {sections.map((section, index) => (
              <div key={index}>
                <motion.section
                  id={`section-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-12 lg:mb-16 scroll-mt-32"
                >
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {section.title}
                  </h2>
                  <div className="space-y-6 mb-8">
                    {section.content
                      .split('\n\n')
                      .filter((p) => p.trim() !== '')
                      .map((paragraph, i) => (
                        <p
                          key={i}
                          className="text-base lg:text-lg text-white/80 leading-relaxed"
                          style={{ whiteSpace: 'pre-line' }}
                        >
                          {paragraph}
                        </p>
                      ))}
                  </div>

                  {/* Section Image */}
                  {section.image && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="rounded-3xl overflow-hidden mt-8"
                    >
                      <img
                        src={section.image}
                        alt={section.title}
                        className="w-full h-[300px] lg:h-[500px] object-cover"
                      />
                    </motion.div>
                  )}
                </motion.section>
              </div>
            ))}

            {/* Conclusion */}
            <motion.div
              id="conclusion"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12 lg:mb-16 scroll-mt-32"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Key Takeaways</h3>
            </motion.div>

            {/* Key Takeaways List */}
            <div
              className={`p-8 lg:p-12 backdrop-blur-sm border-2 border-white/20 rounded-3xl scroll-mt-32`}
              style={themeStyles.takeaways}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-white/80 flex-shrink-0 mt-1" />
                <div className="space-y-4">
                  {takeaways
                    .split('\n\n')
                    .filter((p) => p.trim() !== '')
                    .map((paragraph, i) => (
                      <p
                        key={i}
                        className="text-base lg:text-lg text-white/80 leading-relaxed"
                        style={{ whiteSpace: 'pre-line' }}
                      >
                        {paragraph}
                      </p>
                    ))}
                </div>
              </div>
            </div>

            {/* Sources & References */}
            {sources && sources.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-12 pt-12 border-t border-white/10"
              >
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2 mt-4">
                  <Link2 className="w-5 h-5 text-white/60" />
                  Sources & References
                </h3>
                <ul className="space-y-3">
                  {sources.map((source, index) => (
                    <li key={index}>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-2 text-white hover:text-purple-400 transition-colors duration-300 w-fit"
                      >
                        <span className="border-b border-transparent group-hover:border-purple-400">
                          {source.title}
                        </span>
                        <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Share/CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-24 lg:mt-32 text-center"
            >
              <p className="text-white mb-6 mt-12">
                Found this article helpful? Share it with your network!
              </p>
              <div className="text-center mt-12 mb-20">
                <Link
                  href="/resources/"
                  className={`inline-block px-8 py-4 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 transform duration-200`}
                  style={themeStyles.button}
                >
                  Explore More Resources
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </article>

      <ResponsiveFooter />
    </div>
  );
}
